import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [familyProfile, setFamilyProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) loadFamilyProfile(session.user);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) loadFamilyProfile(session.user);
      else {
        setFamilyProfile(null);
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadFamilyProfile = async (user) => {
    try {
      const isSA = user.email?.toLowerCase() === 'lorzen@gmx.de';
      setIsSuperAdmin(isSA);

      const { data: memberData } = await supabase
        .from('family_members')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (memberData) {
        const profile = {
          ...memberData,
          checkoutUrls: memberData.checkoutUrls || {},
          settings: memberData.settings || {}
        };
        setFamilyProfile(profile);
        setIsAdmin(memberData.role === 'admin' || isSA);
      } else if (isSA) {
        setIsAdmin(true);
      }
    } catch (err) {
      console.error("Auth: Profile Load Error", err);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    setAuthError(null);
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (result.error) setAuthError(result.error.message);
    return result;
  };

  const signUp = async (email, password, fullName) => {
    setAuthError(null);
    const result = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        data: { full_name: fullName }
      }
    });
    if (result.error) setAuthError(result.error.message);
    return result;
  };

  const signInWithGoogle = async () => {
    setAuthError(null);
    const result = await supabase.auth.signInWithOAuth({ provider: 'google' });
    if (result.error) setAuthError(result.error.message);
    return result;
  };

  const signInWithMagicLink = async (email) => {
    setAuthError(null);
    const result = await supabase.auth.signInWithOtp({ email });
    if (result.error) setAuthError(result.error.message);
    return result;
  };

  const resetPassword = async (email) => {
    setAuthError(null);
    const result = await supabase.auth.resetPasswordForEmail(email);
    if (result.error) setAuthError(result.error.message);
    return result;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.hash = '#/';
  };

  return (
    <AuthContext.Provider value={{ 
      user, loading, familyProfile, isAdmin, isSuperAdmin, signOut,
      signIn, signUp, signInWithGoogle, signInWithMagicLink, resetPassword,
      authError,
      familyId: familyProfile?.family_id 
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);